import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Target, Users, Zap } from "lucide-react";
import { Helmet } from "react-helmet-async";

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us - Helping 50K+ Travelers Save Time Since 2024</title>
        <meta name="description" content="🎯 Airport Buddy helps 50K+ travelers save 30+ minutes at security daily with real-time TSA wait times and flight status." />
        <link rel="canonical" href="https://airportbuddy.app/about" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About Airport Buddy — Our Mission" />
        <meta property="og:description" content="Airport Buddy helps 50K+ travelers save time at security with real-time TSA wait times and flight status." />
        <meta property="og:url" content="https://airportbuddy.app/about" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>

          <div className="max-w-3xl mx-auto space-y-8">
            <header className="text-center space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                About Airport Wait Buddy
              </h1>
              <p className="text-xl text-muted-foreground">
                Making airport travel stress-free, one traveler at a time
              </p>
            </header>

            <Card>
              <CardContent className="pt-6 space-y-6">
                <section>
                  <h2 className="text-2xl font-semibold mb-3">Our Mission</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Airport Wait Buddy was created to solve a common travel frustration: uncertainty. 
                    Not knowing how long you'll wait in security lines, whether your flight is on time, 
                    or where to grab a quick meal can turn travel into a stressful experience. We believe 
                    that with the right information at your fingertips, airport travel can be smooth, 
                    efficient, and even enjoyable.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">What We Do</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Airport Wait Buddy provides real-time information for major US airports, including:
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Live TSA security checkpoint wait times</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Real-time flight status and gate information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Restaurant locations and availability</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Terminal navigation and airport information</span>
                    </li>
                  </ul>
                </section>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <Target className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Our Vision</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    To be every traveler's essential companion, providing accurate, 
                    real-time airport information that saves time and reduces stress.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <Zap className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Real-Time Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    We aggregate data from multiple sources to provide the most 
                    accurate and up-to-date airport information available.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <Users className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>For Travelers</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Built by travelers, for travelers. We understand the challenges 
                    of modern air travel and designed our app to address them.
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="pt-6">
                <h2 className="text-2xl font-semibold mb-4">Why Choose Airport Wait Buddy?</h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong className="text-foreground">Comprehensive Coverage:</strong> We support all major US airports, 
                    ensuring you have reliable information wherever you travel.
                  </p>
                  <p>
                    <strong className="text-foreground">Always Up-to-Date:</strong> Our real-time data means you're never 
                    working with outdated information.
                  </p>
                  <p>
                    <strong className="text-foreground">Easy to Use:</strong> Simple, intuitive interface designed for 
                    quick access when you're on the go.
                  </p>
                  <p>
                    <strong className="text-foreground">Free to Use:</strong> Access essential travel information without 
                    subscription fees or hidden costs.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="text-center space-y-4">
              <h2 className="text-2xl font-semibold">Ready to Travel Smarter?</h2>
              <Button asChild size="lg">
                <Link to="/auth">Get Started Free</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
